import React, { useState, useContext, useRef, useEffect, useMemo } from "react";
import { NavLink } from "react-router-dom";
import Moment from "react-moment";

//pages & includes
import ManageSidebar from "../ManageSidebar";

//functions
import { _t, getCookie } from "../../../../../functions/Functions";
import { useTranslation } from "react-i18next";

//axios and base url
import axios from "axios";
import { BASE_URL } from "../../../../../BaseUrl";

//3rd party packages
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";

//context consumer
import { SettingsContext } from "../../../../../contexts/Settings";
import { UserContext } from "../../../../../contexts/User";
import DataTable from "../../../../common/DataTable";

const toastObj = {
  position: "bottom-center",
  autoClose: 10000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  className: "text-center toast-notification",
};

const StoreRequisitionDept = () => {
  const { t } = useTranslation();
  const searchInputRef = useRef(null);

  //getting context values here
  let {
    loading,
    setLoading,
  } = useContext(SettingsContext);

  let {
    //store requisition dept
    storeRequisitionDeptList,
    setStoreRequisitionDeptList,
    setPaginatedStoreRequisitionDept,
    storeRequisitionDeptForSearch,
    setStoreRequisitionDeptForSearch,

    //pagination
    dataPaginating,
  } = useContext(UserContext);

  useEffect(() => {
    if (setPaginatedStoreRequisitionDept) {
      setPaginatedStoreRequisitionDept(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //search result
  let [searchedStoreRequisition, setSearchedStoreRequisition] = useState({
    list: null,
    searched: false,
  });

  //search customers here
  const handleSearch = (e) => {
    let searchInput = e.target.value.toLowerCase();
    if (searchInput.length === 0 || storeRequisitionDeptForSearch === null) {
      setSearchedStoreRequisition({
        ...searchedStoreRequisition,
        searched: false,
      });
    } else {
      let searchedList = storeRequisitionDeptForSearch.filter((item) => {
        let lowerCaseDocumentNo = item.document_identity
          ? item.document_identity.toLowerCase()
          : "";
        let lowerCaseDocumentDate = item.document_date
          ? item.document_date.toLowerCase()
          : "";
        let lowerCaseRemarks = item.remarks ? item.remarks.toLowerCase() : "";
        let fromDept = item?.from_dept?.name
          ? item.from_dept.name.toLowerCase()
          : "";
        let toDept = item?.to_dept?.name
          ? item.to_dept.name.toLowerCase()
          : "";
        let branch = item?.branch?.name
          ? item.branch.name.toLowerCase()
          : item?.branch_name
          ? item.branch_name.toLowerCase()
          : "";

        return (
          lowerCaseDocumentNo.includes(searchInput) ||
          lowerCaseDocumentDate.includes(searchInput) ||
          fromDept.includes(searchInput) ||
          toDept.includes(searchInput) ||
          branch.includes(searchInput) ||
          lowerCaseRemarks.includes(searchInput)
        );
      });
      setSearchedStoreRequisition({
        ...searchedStoreRequisition,
        list: searchedList,
        searched: true,
      });
    }
  };

  const onClearSearch = () => {
    setSearchedStoreRequisition({
      ...searchedStoreRequisition,
      searched: false,
    });
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
  };

  const handleFetchData = (page) => {
    setPaginatedStoreRequisitionDept(page);
  };

  const handlePostApi = async (id) => {
    setLoading(true);
    const url = BASE_URL + "/settings/requistion-dept-post";
    const formData = {
      id: id,
      is_post: 1,
    };
    await axios
      .post(url, formData, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      })
      .then((res) => {
        setStoreRequisitionDeptList(res.data[0]);
        setStoreRequisitionDeptForSearch(res.data[1]);
        setSearchedStoreRequisition({
          ...searchedStoreRequisition,
          list: res.data[1],
        });
        setLoading(false);
        toast.success(`${_t(t("Stock requisition has been post"))}`, toastObj);
      })
      .catch((error) => {
        setLoading(false);
        if (error.response?.data?.message) {
          toast.error(`${_t(t(error.response.data.message))}`, toastObj);
          return;
        }
        toast.error(`${_t(t("Please try again"))}`, toastObj);
      });
  };

  const handleDeleteConfirmation = (id) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="card card-body">
            <h1>{_t(t("Are you sure?"))}</h1>
            <p className="text-center">{_t(t("You want to delete this?"))}</p>
            <div className="d-flex justify-content-center">
              <button
                className="btn btn-primary"
                onClick={() => {
                  handleDelete(id);
                  onClose();
                }}
              >
                {_t(t("Yes, delete it!"))}
              </button>
              <button className="btn btn-success ml-2 px-3" onClick={onClose}>
                {_t(t("No"))}
              </button>
            </div>
          </div>
        );
      },
    });
  };

  const handleDelete = (id) => {
    setLoading(true);
    const url = BASE_URL + `/settings/store-requisition-dept/${id}`;
    axios
      .delete(url, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      })
      .then(() => {
        setLoading(false);
        toast.success(
          `${_t(t("Store requisition has been deleted successfully"))}`,
          toastObj
        );
        if (setPaginatedStoreRequisitionDept) {
          setPaginatedStoreRequisitionDept(currentPage || 1);
        }
      })
      .catch((err) => {
        setLoading(false);
        if (err.response?.data?.message) {
          toast.error(`${_t(t(err.response.data.message))}`, toastObj);
          return;
        }
        toast.error(`${_t(t("Please try again"))}`, toastObj);
      });
  };

  const getListData = () => {
    if (searchedStoreRequisition.searched) {
      return searchedStoreRequisition.list || [];
    }
    return storeRequisitionDeptList?.data || [];
  };

  const currentPage = storeRequisitionDeptList?.current_page;
  const perPage = storeRequisitionDeptList?.per_page;
  const isSearched = searchedStoreRequisition.searched;

  const columns = useMemo(
    () => [
      {
        label: _t(t("S/L")),
        render: (item, i) =>
          i +
          1 +
          ((currentPage || 1) - 1) *
            (perPage || 10),
      },
      {
        label: _t(t("Document Number")),
        render: (item) => item.document_identity || item.document_no || "-",
      },
      {
        label: _t(t("Document Date")),
        render: (item) => item.document_date || item.date || "-",
      },
      {
        label: _t(t("Branch")),
        render: (item) => item?.branch?.name || item?.branch_name || "-",
      },
      {
        label: _t(t("From Department")),
        render: (item) =>
          item?.from_department?.name ||
          item?.from_dept?.name ||
          item?.from_dept_name ||
          "-",
      },
      {
        label: _t(t("To Department")),
        render: (item) =>
          item?.to_department?.name ||
          item?.to_dept?.name ||
          item?.to_dept_name ||
          "-",
      },
      {
        label: _t(t("Remarks")),
        render: (item) => item.remarks || "-",
      },
      {
        label: _t(t("Action")),
        render: (item) => (
          <div className="d-flex justify-content-center">
            {item?.is_post === 0 ? (
              <>
                <button
                  className="btn btn-dark ml-2 rounded-sm del-btn"
                  title="Post requisition"
                  style={{
                    padding: "1px 0px 0px 1px",
                  }}
                  onClick={() => handlePostApi(item.id)}
                >
                  <i className="fa fa-check-square-o" aria-hidden="true"></i>
                </button>
                <button
                  className="btn btn-danger ml-2 rounded-sm del-btn"
                  title="Delete requisition"
                  style={{
                    padding: "1px 0px 0px 1px",
                  }}
                  onClick={() => handleDeleteConfirmation(item.id)}
                >
                  <i className="fa fa-trash-o" aria-hidden="true"></i>
                </button>
              </>
            ) : null}
            <NavLink
              className="btn btn-primary ml-2 rounded-sm"
              to={`/dashboard/manage/stock/store-requisition-dept-edit/${item.id}`}
              style={{
                height: "1.7rem",
                width: "1.7rem",
              }}
            >
              <div className="d-flex align-items-center justify-content-center">
                <i
                  className={`${
                    item?.is_post ? "fa fa-eye" : "fa fa-pencil"
                  } edit-icon`}
                  aria-hidden="true"
                ></i>
              </div>
            </NavLink>
          </div>
        ),
      },
      {
        label: _t(t("Created By")),
        render: (item) => item?.created_by_name || "-",
      },
      {
        label: _t(t("Created At")),
        render: (item) =>
          item?.created_at ? (
            <Moment format="LL">{item.created_at}</Moment>
          ) : (
            "-"
          ),
      },
      {
        label: _t(t("Updated By")),
        render: (item) => item?.updated_by_name || "-",
      },
      {
        label: _t(t("Updated At")),
        render: (item) =>
          item?.updated_at ? (
            <Moment format="LL">{item.updated_at}</Moment>
          ) : (
            "-"
          ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, isSearched, currentPage, perPage]
  );

  return (
    <>
      <Helmet>
        <title>{_t(t("Store Requisition Dept"))}</title>
      </Helmet>

      {/* main body */}
      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            {/* left Sidebar */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ManageSidebar />
            </div>
            {/* left Sidebar ends */}

            {/* Rightbar contents */}
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="t-bg-white">
                <div className="t-pl-15 t-pr-15">
                  <div className="row gx-2 align-items-center t-pt-15 t-pb-15">
                    <div className="col-md-6 col-lg-5 t-mb-15 mb-md-0">
                      <ul className="t-list fk-breadcrumb">
                        <li className="fk-breadcrumb__list">
                          <span className="t-link fk-breadcrumb__link text-capitalize">
                            {!searchedStoreRequisition.searched
                              ? _t(t("Store Requisition Dept List"))
                              : _t(t("Search Result"))}
                          </span>
                        </li>
                      </ul>
                    </div>
                    <div className="col-md-6 col-lg-7">
                      <div className="row gx-3 align-items-center">
                        {/* Search store requisition dept */}
                        <div className="col-md-9 t-mb-15 mb-md-0">
                          <div className="input-group">
                            <div className="form-file">
                              <input
                                type="text"
                                className="form-control border-0 form-control--light-1 rounded-left"
                                placeholder={_t(t("Search")) + ".."}
                                onChange={handleSearch}
                                ref={searchInputRef}
                              />
                            </div>
                            <button
                              className="btn btn-primary rounded-right"
                              type="button"
                            >
                              <i
                                className="fa fa-search"
                                aria-hidden="true"
                              ></i>
                            </button>
                          </div>
                        </div>

                        {/* Add button */}
                        <div className="col-md-3 text-md-right">
                          <NavLink
                            to="/dashboard/manage/stock/store-requisition-dept-add"
                            className="btn btn-primary xsm-text text-uppercase btn-lg btn-block rounded"
                          >
                            {_t(t("add new"))}
                          </NavLink>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <DataTable
                  loading={loading || dataPaginating}
                  data={getListData()}
                  columns={columns}
                  emptyText="No data available"
                  pagination={{
                    isPageLoading: dataPaginating,
                    isPagination: true,
                    pageCount: storeRequisitionDeptList?.last_page,
                    currentPage: storeRequisitionDeptList?.current_page,
                    from: storeRequisitionDeptList?.from,
                    to: storeRequisitionDeptList?.to,
                    total: storeRequisitionDeptList?.total,
                  }}
                  handleFetchData={handleFetchData}
                  searchPayload={{
                    isSearched: searchedStoreRequisition.searched,
                    onClearSearch,
                  }}
                />
              </div>
            </div>
            {/* Rightbar contents end*/}
          </div>
        </div>
      </main>
      {/* main body ends */}
    </>
  );
};

export default StoreRequisitionDept;
